import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { TabPanel, TabView, TabViewTabChangeEvent } from 'primereact/tabview';
import { classNames } from 'primereact/utils';
import {
    ScheduleHoursDTO,
    ServiceStatisticsBenchmarkDTO,
    ServiceStatisticsFilter,
    ServiceStatisticsGranularity,
    ServiceStatisticsHourlyDTO,
    ServiceStatisticsOccupancyDTO,
    ServiceStatisticsOptionsDTO,
    ServiceStatisticsPriorityDTO,
    ServiceStatisticsServiceDTO,
    ServiceStatisticsSummaryDTO,
    ServiceStatisticsTimelineRowDTO,
    WaitRankingRowDTO,
} from '../../dto/ServiceStatisticsDTO';
import {
    exportServiceStatistics,
    getServiceStatisticsBenchmark,
    getServiceStatisticsHourly,
    getServiceStatisticsOccupancy,
    getServiceStatisticsOptions,
    getServiceStatisticsPriorities,
    getServiceStatisticsScheduleHours,
    getServiceStatisticsServices,
    getServiceStatisticsSummary,
    getServiceStatisticsTimeline,
    getServiceStatisticsWaitRanking,
    StatisticsAccessProblem,
    statisticsAccessProblem,
} from '../../services/ServiceStatisticsController';
import { parseApiError } from '../../services/apiError';
import ExportMenu, { ExportFormat } from '../../components/novarium/ExportMenu';
import NvTooltips from '../../components/novarium/NvTooltips';
import { PERIOD_PRESET_NAME_ID as PRESET_NAME_ID } from '../../components/novarium/periodRange';
import AlertsDialog, { Wait60Notice } from './AlertsDialog';
import { ALERT_MIN_ATTENTIONS, hasWait60Notice, WaitLevel, WAIT_RANKING_ROWS } from './alertThresholds';
import { RANKING_LEVEL } from './waitLevelText';
import { FilterField, FilterSelection, filterOptions, filterVisibility, reconcileSelection } from './filterOptions';
import GranularitySelector from './GranularitySelector';
import HourlyTab from './HourlyTab';
import MeasureSelector from './MeasureSelector';
import OccupancyPanel from './OccupancyPanel';
import PriorityList from './PriorityList';
import { ScheduleHoursStatus } from './ScheduleHoursSection';
import ServicesTable from './ServicesTable';
import SpecialityBenchmark from './SpecialityBenchmark';
import StatisticsFilters, {
    DateField, FilterChip, FilterKey, FilterOption, FiltersDraft, PeriodChoice, TemplateTypeFilter,
} from './StatisticsFilters';
import { modeMessageId, StatisticsMode } from './statisticsMode';
import StatisticsKpis from './StatisticsKpis';
import PeriodDetailSidebar from './PeriodDetailSidebar';
import { PeriodTarget } from './periodDetail';
import TimelineEvolution from './TimelineEvolution';
import WaitRanking, { WaitRankingStatus } from './WaitRanking';
import {
    activePreset,
    ALL,
    autoGranularity,
    buildTimeline,
    countDays,
    isoDate,
    MAX_DAYS_BY_WEEK,
    Measure,
    formatShortDate,
    parseIsoDate,
    TimelinePoint,
    periodNeedsYear,
    PeriodPreset,
    presetRange,
    scopeOccupancy,
    scopeToService,
    sumOccupancy,
    toServiceRow,
} from './statisticsUtils';
import './ServicesStatisticsView.scss';

type FieldErrors = Partial<Record<DateField, string>>;

const sameErrors = (a: FieldErrors, b: FieldErrors): boolean => a.initialDate === b.initialDate && a.finalDate === b.finalDate;

/** Lo que se puede filtrar tocando una fila, un código o una fila del ranking: el servicio y el código de atención. */
type ScopeKey = 'serviceId' | 'priorityId';

/** Los filtros que cambian de una (fila, ranking, código y chips): los de ScopeKey y los chips de tipo, especialidad y profesional. */
type ScopePatch = Partial<Pick<FiltersDraft, FilterKey>>;

/** Lo que se consultó: fechas ya validadas. */
interface Query {
    initialDate: string;
    finalDate: string;
    templateType: TemplateTypeFilter;
    specialityId: number;
    professionalId: number;
    serviceId: number;
    priorityId: number;
}

/** La evolución que se está mostrando y con qué rango y agrupación se consultó (no siempre es la de `applied`). */
interface TimelineData {
    rows: ServiceStatisticsTimelineRowDTO[];
    granularity: ServiceStatisticsGranularity;
    initialDate: string;
    finalDate: string;
}

interface StatisticsData {
    summary: ServiceStatisticsSummaryDTO;
    hourly: ServiceStatisticsHourlyDTO;
    services: ServiceStatisticsServiceDTO[];
    priorities: ServiceStatisticsPriorityDTO[];
    /** Agendas de consultorios con su ocupación (las del tipo y la especialidad, sin filtro de servicio ni de código). */
    occupancy: ServiceStatisticsOccupancyDTO[];
    /** La ocupación no se pudo consultar; el resto de los datos sí. */
    occupancyFailed: boolean;
    /** Solo en «Mi desempeño»: el promedio de la especialidad del profesional. */
    benchmark: ServiceStatisticsBenchmarkDTO | null;
    /** La comparación no se pudo consultar; el resto de los datos sí. */
    benchmarkFailed: boolean;
}

export interface StatisticsDashboardProps {
    mode: StatisticsMode;
}

/** Cuántas filas se piden del ranking: alcanza para «Ver los N avisos» (el backend recorta a esta cantidad). */
const RANKING_FETCH_LIMIT = 100;

/** Cuánto se espera antes de consultar cuando los filtros cambian seguido (varios selects, una búsqueda en la lista). */
const DEBOUNCE_MS = 300;

const PERIOD_UNIT_ID: Record<ServiceStatisticsGranularity, string> = {
    DAY: 'ServicesStatisticsPeriodDay',
    WEEK: 'ServicesStatisticsPeriodWeek',
    MONTH: 'ServicesStatisticsPeriodMonth',
};

const MEASURE_NAME_ID: Record<Measure, string> = {
    attentions: 'ServicesStatisticsMeasureAttentions',
    wait: 'ServicesStatisticsMeasureWait',
    service: 'ServicesStatisticsMeasureService',
};

/** Los filtros que se acomodan entre sí (los cuatro de `FilterSelection`). */
const FILTER_FIELDS: FilterField[] = ['templateType', 'specialityId', 'professionalId', 'serviceId'];

/** Los chips de "Filtrando por" en el orden de la fila de filtros. */
const CHIP_ORDER: FilterKey[] = ['templateType', 'specialityId', 'professionalId', 'serviceId', 'priorityId'];

const CHIP_LABEL_ID: Record<FilterKey, string> = {
    templateType: 'ServicesStatisticsChipType',
    specialityId: 'ServicesStatisticsChipSpeciality',
    professionalId: 'ServicesStatisticsChipProfessional',
    serviceId: 'ServicesStatisticsChipService',
    priorityId: 'ServicesStatisticsChipPriority',
};

/** Las pestañas, en el orden en que se ofrecen. */
const TAB_SERVICES = 0;
const TAB_AGENDAS = 3;

/** Tipo de las agendas de consultorio (`templateType` 1): solo ellas tienen un horario programado para comparar. */
const ROOM_TYPE = 1;

const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, 'es');

function defaultDraft(today: Date): FiltersDraft {
    const { from, to } = presetRange('month30', today);
    return { from, to, templateType: ALL, specialityId: ALL, professionalId: ALL, serviceId: ALL, priorityId: ALL };
}

function toQuery(draft: FiltersDraft): Query {
    return {
        initialDate: isoDate(draft.from!),
        finalDate: isoDate(draft.to!),
        templateType: draft.templateType,
        specialityId: draft.specialityId,
        professionalId: draft.professionalId,
        serviceId: draft.serviceId,
        priorityId: draft.priorityId,
    };
}

const sameQuery = (a: Query, b: Query): boolean =>
    a.initialDate === b.initialDate && a.finalDate === b.finalDate && a.templateType === b.templateType
    && a.specialityId === b.specialityId && a.professionalId === b.professionalId && a.serviceId === b.serviceId
    && a.priorityId === b.priorityId;

/** `mine` es de la pantalla, no del filtro: en «Mi desempeño» viaja siempre y el backend fuerza al profesional del usuario. */
function toFilter(query: Query, mine: boolean): ServiceStatisticsFilter {
    return {
        initialDate: query.initialDate,
        finalDate: query.finalDate,
        templateType: query.templateType || undefined,
        specialityId: query.specialityId || undefined,
        professionalId: query.professionalId || undefined,
        serviceId: query.serviceId || undefined,
        priorityId: query.priorityId || undefined,
        mine: mine || undefined,
    };
}

/** Los filtros que van dentro de «Más filtros». */
const MORE_FILTERS: FilterField[] = ['templateType', 'specialityId', 'professionalId'];

/**
 * Tablero de atenciones, espera y duración de servicios y consultorios en un período, comparadas contra el período
 * anterior. Lo comparten dos pantallas: «Estadísticas de atención» (`management`: todo el sitio, o un profesional a
 * elección si el usuario tiene la acción de ver a todos) y «Mi desempeño» (`mine`: solo lo del profesional del usuario,
 * con textos en primera persona, sin los filtros Tipo y Profesional y con la comparación contra el promedio de su
 * especialidad).
 * Arriba, los filtros (todos al instante: no hay «Consultar»), una frase que resume el período, cuatro indicadores y
 * «Dónde hay más espera», el ranking con la causa de cada una; después, en pestañas, el detalle por servicio, la
 * evolución, los horarios, la ocupación y los códigos de atención. Tocar un servicio, una agenda, un código o «Ver
 * agenda» (la fila del ranking) filtra toda la pantalla de la misma manera (un chip «Filtrando por»).
 * Toda la consulta es del sitio del usuario y se calcula en el backend; acá solo se ordena, se filtra la tabla y se
 * comparan los indicadores de cada servicio contra los umbrales de `alertThresholds.ts`.
 * Si el backend responde que el usuario no tiene acceso (o, en «Mi desempeño», que no es un profesional), la pantalla
 * muestra ese estado en lugar de los filtros y los datos, sin card flotante. Si una consulta falla, un solo aviso en el
 * cuerpo con «Reintentar».
 */
export default function StatisticsDashboard({ mode }: StatisticsDashboardProps) {
    const intl = useIntl();
    const today = useMemo(() => new Date(), []);
    const mine = mode === 'mine';

    const [options, setOptions] = useState<ServiceStatisticsOptionsDTO | null>(null);
    /** Lo que muestran los controles (las fechas pueden estar vacías o a medio escribir). */
    const [draft, setDraft] = useState<FiltersDraft>(() => defaultDraft(today));
    const draftRef = useRef(draft);
    const [errors, setErrors] = useState<FieldErrors>({});
    /** Última consulta pedida: al cambiar, se vuelve a cargar todo. */
    const [query, setQuery] = useState<Query>(() => toQuery(defaultDraft(today)));
    const queryRef = useRef(query);
    /** Hay un cambio de filtros esperando el debounce: los datos que se ven ya no son los de los filtros. */
    const [pending, setPending] = useState(false);
    const debounceRef = useRef<number | undefined>(undefined);
    /** Consulta de los datos que se están mostrando. */
    const [applied, setApplied] = useState<Query | null>(null);
    const [data, setData] = useState<StatisticsData | null>(null);
    const [loading, setLoading] = useState(true);
    /** La consulta principal (u opciones) falló: un solo aviso en el cuerpo, con «Reintentar». */
    const [loadError, setLoadError] = useState(false);
    /** Las opciones de los filtros no se pudieron cargar: es el mismo aviso, aunque los datos sí lleguen. */
    const [optionsFailed, setOptionsFailed] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [moreOpen, setMoreOpen] = useState(false);
    /** Eligió «Personalizado» en «Período»: las fechas se muestran aunque coincidan con un atajo. */
    const [customPeriod, setCustomPeriod] = useState(false);
    const [alertsOpen, setAlertsOpen] = useState(false);
    const [measure, setMeasure] = useState<Measure>('attentions');
    /** Evolución mostrada. Se consulta aparte del resto: cambiar «Agrupar por» no vuelve a pedir los demás datos. */
    const [timelineData, setTimelineData] = useState<TimelineData | null>(null);
    const [timelineLoading, setTimelineLoading] = useState(false);
    /** La evolución no se pudo consultar; el resto de los datos sí. */
    const [timelineFailed, setTimelineFailed] = useState(false);
    const [timelineReload, setTimelineReload] = useState(0);
    /** Lo que eligió el usuario en «Agrupar por», para el rango en que lo eligió: manda hasta que el rango cambia. */
    const [granularityChoice, setGranularityChoice] =
        useState<{ range: string; value: ServiceStatisticsGranularity } | null>(null);
    /** La pestaña activa no se pierde al cambiar los filtros: es de la pantalla, no de los datos. */
    const [activeTab, setActiveTab] = useState(TAB_SERVICES);
    /**
     * «Dónde hay más espera»: el backend calcula las filas, su nivel y sus causas. Se consulta aparte del resto (como la
     * evolución): si falla, solo el ranking avisa, con «Reintentar». Se piden hasta `RANKING_FETCH_LIMIT` filas, sin el
     * filtro de servicio: la pantalla acota a la agenda elegida y el ranking toma las primeras cinco.
     */
    const [rankingRows, setRankingRows] = useState<WaitRankingRowDTO[] | null>(null);
    const [rankingTotal, setRankingTotal] = useState(0);
    const [rankingLoading, setRankingLoading] = useState(false);
    const [rankingFailed, setRankingFailed] = useState(false);
    const [rankingReload, setRankingReload] = useState(0);
    /**
     * Horario programado contra el real de la agenda elegida (pestaña «Horarios»). Se consulta aparte y solo cuando se ve
     * la pestaña: `data` null es que la consulta falló. Cada resultado guarda la consulta de la que salió.
     */
    const [scheduleResult, setScheduleResult] = useState<{ key: string; data: ScheduleHoursDTO | null } | null>(null);
    const [scheduleReload, setScheduleReload] = useState(0);
    /** El backend no deja ver la pantalla a este usuario: se muestra el motivo en lugar de la pantalla vacía. */
    const [accessProblem, setAccessProblem] = useState<StatisticsAccessProblem | null>(null);
    /** El período de la evolución cuyo detalle está abierto en el panel lateral (null: cerrado), y quién lo abrió. */
    const [detail, setDetail] = useState<PeriodTarget | null>(null);
    const detailOpenerRef = useRef<HTMLElement | null>(null);
    /**
     * «Ver solo este día» cambió el período de la pantalla: de dónde venía (los filtros y cómo se llamaba), para ofrecer
     * «Volver a …» mientras se siga viendo ese rango. Sin cambios en `StatisticsFilters`: el enlace va bajo los filtros.
     */
    const [rangeOrigin, setRangeOrigin] =
        useState<{ previousFrom: Date | null; previousTo: Date | null; custom: boolean; label: string; from: string; to: string } | null>(null);

    useEffect(() => () => window.clearTimeout(debounceRef.current), []);

    useEffect(() => {
        let cancelled = false;
        setOptionsFailed(false);
        getServiceStatisticsOptions(mine)
            .then(result => { if (!cancelled) setOptions(result); })
            .catch((error: unknown) => {
                if (cancelled) return;
                const problem = statisticsAccessProblem(parseApiError(error));
                if (problem) setAccessProblem(problem);
                else setOptionsFailed(true);
            });
        return () => { cancelled = true; };
    }, [mine, reloadKey]);

    const rangeKey = `${query.initialDate}|${query.finalDate}`;
    const queryDays = useMemo(() => countDays(parseIsoDate(query.initialDate), parseIsoDate(query.finalDate)),
        [query.initialDate, query.finalDate]);
    /** La del usuario si la eligió para este rango; si no, la que hace legible el gráfico según el largo del rango. */
    const granularity: ServiceStatisticsGranularity =
        granularityChoice?.range === rangeKey ? granularityChoice.value : autoGranularity(queryDays);

    useEffect(() => {
        let cancelled = false;
        const filter = toFilter(query, mine);
        setLoading(true);
        setLoadError(false);
        Promise.all([
            getServiceStatisticsSummary(filter),
            getServiceStatisticsHourly(filter),
            getServiceStatisticsServices(filter),
            getServiceStatisticsPriorities(filter),
            // Si falla, el resto de la pantalla sigue y la ocupación avisa con un texto (sin toast)
            getServiceStatisticsOccupancy(filter)
                .then(list => ({ list, failed: false }))
                .catch((error: unknown) => {
                    parseApiError(error);
                    return { list: [] as ServiceStatisticsOccupancyDTO[], failed: true };
                }),
            // Solo «Mi desempeño». Si falla, la card de comparación avisa con un texto y el resto sigue
            mine
                ? getServiceStatisticsBenchmark({
                    initialDate: filter.initialDate, finalDate: filter.finalDate, specialityId: filter.specialityId,
                })
                    .then(benchmark => ({ benchmark, failed: false }))
                    .catch((error: unknown) => {
                        parseApiError(error);
                        return { benchmark: null, failed: true };
                    })
                : Promise.resolve({ benchmark: null, failed: false }),
        ])
            .then(([summary, hourly, services, priorities, occupancy, benchmark]) => {
                if (cancelled) return;
                setData({
                    summary, hourly, services, priorities,
                    occupancy: occupancy.list, occupancyFailed: occupancy.failed,
                    benchmark: benchmark.benchmark, benchmarkFailed: benchmark.failed,
                });
                setApplied(query);
                setErrors(current => (sameErrors(current, {}) ? current : {}));
            })
            .catch((error: unknown) => {
                if (cancelled) return;
                // Errores de un dato (fechas) van al campo; el resto, un solo aviso en el cuerpo (sin card flotante).
                const apiError = parseApiError(error);
                const problem = statisticsAccessProblem(apiError);
                if (problem) {
                    setAccessProblem(problem);
                    return;
                }
                const fieldErrors: FieldErrors = {};
                for (const detail of apiError.details ?? []) {
                    if (detail.field === 'initialDate' || detail.field === 'finalDate') fieldErrors[detail.field] = detail.message;
                }
                if (Object.keys(fieldErrors).length > 0) {
                    setErrors(fieldErrors);
                } else {
                    setData(null);
                    setApplied(null);
                    setLoadError(true);
                }
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [query, mine, reloadKey]);

    // La evolución va aparte: con otra agrupación solo ella vuelve a consultarse. Si falla, la pestaña avisa con un texto
    // (sin toast, porque la consulta principal ya avisa) y el resto de la pantalla sigue.
    useEffect(() => {
        let cancelled = false;
        setTimelineLoading(true);
        getServiceStatisticsTimeline(toFilter(query, mine), granularity)
            .then(rows => {
                if (cancelled) return;
                setTimelineData({ rows, granularity, initialDate: query.initialDate, finalDate: query.finalDate });
                setTimelineFailed(false);
            })
            .catch((error: unknown) => {
                if (cancelled) return;
                const problem = statisticsAccessProblem(parseApiError(error));
                if (problem) setAccessProblem(problem);
                else setTimelineFailed(true);
            })
            .finally(() => { if (!cancelled) setTimelineLoading(false); });
        return () => { cancelled = true; };
    }, [query, granularity, mine, timelineReload]);

    // El ranking no depende del servicio elegido: cambiar de agenda no lo vuelve a consultar
    const rankingKey = JSON.stringify(toFilter({ ...query, serviceId: ALL }, mine));
    useEffect(() => {
        let cancelled = false;
        setRankingLoading(true);
        getServiceStatisticsWaitRanking(JSON.parse(rankingKey) as ServiceStatisticsFilter, RANKING_FETCH_LIMIT)
            .then(result => {
                if (cancelled) return;
                setRankingRows(result.rows);
                setRankingTotal(result.total);
                setRankingFailed(false);
            })
            .catch((error: unknown) => {
                if (cancelled) return;
                const problem = statisticsAccessProblem(parseApiError(error));
                if (problem) setAccessProblem(problem);
                else {
                    setRankingRows(null);
                    setRankingFailed(true);
                }
            })
            .finally(() => { if (!cancelled) setRankingLoading(false); });
        return () => { cancelled = true; };
    }, [rankingKey, rankingReload]);

    /**
     * La agenda de consultorio de la que se muestra el horario: la elegida en el filtro o, en «Mis agendas» con una sola,
     * esa. Con un servicio de guardia o sin agenda elegida no hay (0).
     */
    const scheduleAgendaId = useMemo(() => {
        const rooms = (options?.services ?? []).filter(item => item.templateType === ROOM_TYPE);
        if (query.serviceId !== ALL) return rooms.some(item => item.id === query.serviceId) ? query.serviceId : 0;
        return mine && rooms.length === 1 ? rooms[0].id : 0;
    }, [options, query.serviceId, mine]);
    const scheduleKey = scheduleAgendaId === 0 ? '' : JSON.stringify({ filter: toFilter(query, mine), serviceId: scheduleAgendaId });
    /** Agendas de ocupación: la elegida si hay una, o todas las de consultorios. */
    const agendas = useMemo(() => (data && applied ? scopeOccupancy(data.occupancy, applied.serviceId) : []),
        [data, applied]);
    const hasData = !!data && data.summary.current.attentions > 0;
    /**
     * El filtro no deja atenciones pero las agendas sí tienen lugares o turnos (por ejemplo, todos atendidos
     * automáticos): se sigue mostrando la pestaña «Agendas» sola.
     */
    const agendasOnly = useMemo(() => {
        if (!data || data.occupancyFailed || data.summary.current.attentions > 0) return false;
        const totals = sumOccupancy(agendas.map(item => item.current));
        return totals.slots + totals.booked + totals.overbooked + totals.missed + totals.cancelled > 0;
    }, [data, agendas]);
    const agendasTabActive = activeTab === TAB_AGENDAS || agendasOnly;
    const scheduleLoaded = scheduleResult?.key === scheduleKey;

    // Va aparte, como la evolución y el ranking: si carga, falla o viene vacío, solo el bloque de la pestaña lo dice
    useEffect(() => {
        if (scheduleKey === '' || !agendasTabActive || scheduleLoaded) return undefined;
        let cancelled = false;
        const request = JSON.parse(scheduleKey) as { filter: ServiceStatisticsFilter; serviceId: number };
        getServiceStatisticsScheduleHours(request.filter, request.serviceId)
            .then(result => { if (!cancelled) setScheduleResult({ key: scheduleKey, data: result }); })
            .catch((error: unknown) => {
                if (cancelled) return;
                const problem = statisticsAccessProblem(parseApiError(error));
                if (problem) setAccessProblem(problem);
                else setScheduleResult({ key: scheduleKey, data: null });
            });
        return () => { cancelled = true; };
    }, [scheduleKey, agendasTabActive, scheduleLoaded, scheduleReload]);

    const scheduleStatus: ScheduleHoursStatus = scheduleKey === '' ? 'none'
        : !scheduleLoaded ? 'loading' : scheduleResult?.data ? 'ready' : 'failed';
    const retrySchedule = useCallback(() => {
        setScheduleResult(null);
        setScheduleReload(key => key + 1);
    }, []);

    // ── Filtros (todos al instante) ──

    const setDraftNow = useCallback((next: FiltersDraft) => {
        draftRef.current = next;
        setDraft(next);
    }, []);

    const validate = useCallback((next: FiltersDraft): FieldErrors => {
        const result: FieldErrors = {};
        if (!next.from) result.initialDate = intl.formatMessage({ id: 'ServicesStatisticsFromRequired' });
        if (!next.to) result.finalDate = intl.formatMessage({ id: 'ServicesStatisticsToRequired' });
        else if (next.from && next.from > next.to) result.finalDate = intl.formatMessage({ id: 'ServicesStatisticsDateOrder' });
        return result;
    }, [intl]);

    /**
     * Único camino a la consulta: valida las fechas (el error se muestra en el campo y no se consulta) y, si cambió algo,
     * consulta ya (`immediate`: atajos, filas, chips y fechas) o después de `DEBOUNCE_MS` (los selects, que pueden cambiar
     * seguidos). Mientras espera, `pending` marca los datos viejos como tales.
     */
    const applyDraft = useCallback((next: FiltersDraft, immediate: boolean) => {
        window.clearTimeout(debounceRef.current);
        const validationErrors = validate(next);
        // Sin cambios no se vuelve a renderizar: un renderizado de más puede comerse el click sobre el calendario de las fechas
        setErrors(current => (sameErrors(current, validationErrors) ? current : validationErrors));
        if (Object.keys(validationErrors).length > 0) {
            setPending(false);
            return;
        }
        const nextQuery = toQuery(next);
        if (sameQuery(nextQuery, queryRef.current)) {
            setPending(false);
            return;
        }
        const run = () => {
            queryRef.current = nextQuery;
            setQuery(nextQuery);
            setPending(false);
        };
        if (immediate) {
            run();
        } else {
            setPending(true);
            debounceRef.current = window.setTimeout(run, DEBOUNCE_MS);
        }
    }, [validate]);

    /**
     * Único camino de los selects Tipo, Especialidad, Profesional y Servicio o agenda: guarda el valor y deja el resto
     * coherente (lo que ya no corresponde vuelve a «Todos»; ver `reconcileSelection`). Es un solo `setDraft`, así que no
     * hay renders intermedios con una selección incoherente.
     */
    const changeFilter = useCallback((field: FilterField, value: number) => {
        const next = { ...draftRef.current, [field]: value };
        const settled = options ? reconcileSelection(options, next, field, mine) : next;
        setDraftNow(settled);
        applyDraft(settled, false);
    }, [options, mine, setDraftNow, applyDraft]);

    /**
     * Una fecha completa y válida, escrita o elegida en el calendario (o vacía, con «Limpiar»): se guarda y se aplica ya,
     * con un solo gesto. Si Desde queda después de Hasta (o falta una), el error sale en el campo y no se consulta.
     */
    const changeDate = useCallback((field: DateField, value: Date | null) => {
        const next = { ...draftRef.current, [field === 'initialDate' ? 'from' : 'to']: value };
        setDraftNow(next);
        applyDraft(next, true);
    }, [setDraftNow, applyDraft]);

    /** Al salir de los campos de fecha o con Enter: se vuelve a validar y aplicar lo que haya (sin cambios, no hace nada). */
    const commitDates = useCallback(() => applyDraft(draftRef.current, true), [applyDraft]);

    const selectPreset = useCallback((preset: PeriodPreset) => {
        const { from, to } = presetRange(preset, today);
        const next = { ...draftRef.current, from, to };
        setDraftNow(next);
        applyDraft(next, true);
    }, [today, setDraftNow, applyDraft]);

    /** «Período»: un atajo aplica su rango ya; «Personalizado» solo muestra las fechas (conservan el rango actual). */
    const selectPeriod = useCallback((choice: PeriodChoice) => {
        setCustomPeriod(choice === 'custom');
        if (choice !== 'custom') selectPreset(choice);
    }, [selectPreset]);

    /**
     * Único camino para filtrar de una: la fila de la tabla, la agenda del ranking, el código de la lista, «Ver
     * servicio» de un aviso y el ⓧ de un chip terminan acá. Aplica el filtro a toda la pantalla y deja los selects
     * sincronizados.
     */
    const applyScope = useCallback((patch: ScopePatch) => {
        // Si el cambio toca un solo filtro encadenado, ese es el que no se limpia; con varios (Quitar todos) se revisa todo
        const touched = FILTER_FIELDS.filter(field => field in patch);
        const changed = touched.length === 1 ? touched[0] : null;
        const next = { ...draftRef.current, ...patch };
        const settled = options ? reconcileSelection(options, next, changed, mine) : next;
        setDraftNow(settled);
        applyDraft(settled, true);
    }, [options, mine, setDraftNow, applyDraft]);

    /** Tocar el elegido otra vez lo quita; `ALL` (por ejemplo, la fila de la tabla deseleccionada) también. */
    const toggleScope = useCallback((key: ScopeKey, id: number) => {
        applyScope({ [key]: id === ALL || id === draftRef.current[key] ? ALL : id });
    }, [applyScope]);

    const clearFilters = useCallback(() => applyScope({
        templateType: ALL, specialityId: ALL, professionalId: ALL, serviceId: ALL, priorityId: ALL,
    }), [applyScope]);

    const retry = useCallback(() => {
        setLoadError(false);
        setOptionsFailed(false);
        setReloadKey(key => key + 1);
    }, []);

    const allLabel = intl.formatMessage({ id: 'ServicesStatisticsAll' });
    const templateTypeOptions = useMemo<FilterOption[]>(() => [
        { id: ALL, name: allLabel },
        { id: 1, name: intl.formatMessage({ id: 'ServicesStatisticsTypeRooms' }) },
        { id: 2, name: intl.formatMessage({ id: 'ServicesStatisticsTypeServices' }) },
    ], [intl, allLabel]);
    /** Lo coherente con Tipo, Especialidad, Profesional y Servicio ya elegidos, de la única respuesta de `/options`. */
    const selection = useMemo<FilterSelection>(() => ({
        templateType: draft.templateType, specialityId: draft.specialityId,
        professionalId: draft.professionalId, serviceId: draft.serviceId,
    }), [draft.templateType, draft.specialityId, draft.professionalId, draft.serviceId]);
    const choices = useMemo(() => (options ? filterOptions(options, selection) : null), [options, selection]);
    const specialityOptions = useMemo<FilterOption[]>(() => [
        { id: ALL, name: intl.formatMessage({ id: 'ServicesStatisticsAllFeminine' }) },
        ...(choices?.specialities ?? []),
    ], [choices, intl]);
    /** «Todos» y los servicios y agendas del tipo, la especialidad y el profesional elegidos (con profesional: sus agendas y todos los servicios). */
    const serviceOptions = useMemo<FilterOption[]>(() => [
        { id: ALL, name: mine ? intl.formatMessage({ id: 'ServicesStatisticsAllFeminine' }) : allLabel },
        ...[...(choices?.services ?? [])].sort(byName),
    ], [choices, allLabel, mine, intl]);
    /** «Todos» los profesionales y, después, los coherentes con lo elegido, por nombre. Solo los trae el backend con la acción de ver a todos. */
    const professionalOptions = useMemo<FilterOption[]>(() => [
        { id: ALL, name: allLabel },
        ...[...(choices?.professionals ?? [])].sort(byName),
    ], [choices, allLabel]);
    /**
     * Qué selectores se muestran: los que no aportan en el contexto desaparecen (Profesional con Tipo = Servicios o con un
     * servicio elegido y sin profesional, los de una sola opción, Tipo y Profesional en «Mi desempeño»…).
     */
    const visible = useMemo(() => filterVisibility(options, choices, selection, mine), [options, choices, selection, mine]);
    /** Cuántos de los filtros que se ven dentro de «Más filtros» están aplicados. */
    const moreActive = MORE_FILTERS.filter(field => visible[field] && draft[field] !== ALL).length;
    // «Más filtros» se abre solo cuando pasa a haber alguno aplicado; después el usuario puede cerrarlo
    useEffect(() => {
        if (moreActive > 0) setMoreOpen(true);
    }, [moreActive]);

    /** Con un atajo, "25/9 al 24/10" (o solo el día, en «Hoy»); con «Personalizado» están los campos de fecha. */
    const period: PeriodChoice = (customPeriod ? null : activePreset(draft.from, draft.to, today)) ?? 'custom';
    const rangeText = useMemo(() => {
        if (period === 'custom' || !draft.from || !draft.to) return '';
        const withYear = periodNeedsYear(draft.from, draft.to, today);
        const from = formatShortDate(draft.from, withYear);
        const to = formatShortDate(draft.to, withYear);
        return from === to ? from : intl.formatMessage({ id: 'PeriodFieldRange' }, { from, to });
    }, [period, draft.from, draft.to, today, intl]);

    /** Nombre de cada filtro elegido, en el orden de la fila de filtros; solo los que están elegidos. */
    const filterNames = useMemo<Partial<Record<FilterKey, string>>>(() => {
        const unknown = (id: number) => intl.formatMessage({ id: 'ServicesStatisticsChipUnknown' }, { id });
        const result: Partial<Record<FilterKey, string>> = {};
        if (draft.templateType) {
            result.templateType = templateTypeOptions.find(item => item.id === draft.templateType)?.name;
        }
        if (draft.specialityId) {
            result.specialityId = options?.specialities.find(item => item.id === draft.specialityId)?.name
                ?? unknown(draft.specialityId);
        }
        if (draft.professionalId) {
            result.professionalId = options?.professionals.find(item => item.id === draft.professionalId)?.name
                ?? unknown(draft.professionalId);
        }
        if (draft.serviceId) {
            result.serviceId = options?.services.find(item => item.id === draft.serviceId)?.name
                ?? data?.services.find(item => item.serviceId === draft.serviceId)?.serviceName
                ?? unknown(draft.serviceId);
        }
        if (draft.priorityId) {
            result.priorityId = options?.priorities.find(item => item.id === draft.priorityId)?.name
                ?? data?.priorities.find(item => item.priorityId === draft.priorityId)?.name
                ?? unknown(draft.priorityId);
        }
        return result;
    }, [draft.templateType, draft.specialityId, draft.professionalId, draft.serviceId, draft.priorityId, options, data, intl,
        templateTypeOptions]);

    const chips = useMemo<FilterChip[]>(() => CHIP_ORDER.flatMap(key => {
        const name = filterNames[key];
        return name === undefined ? [] : [{ key, label: intl.formatMessage({ id: CHIP_LABEL_ID[key] }, { name }) }];
    }), [filterNames, intl]);

    // ── Datos para dibujar ──

    const timeline = useMemo(() => (timelineData
        ? buildTimeline(timelineData.rows, timelineData.initialDate, timelineData.finalDate, timelineData.granularity)
        : []), [timelineData]);
    /** La agrupación de la evolución que se ve (puede ir un paso atrás de `granularity` mientras se consulta). */
    const shownGranularity = timelineData?.granularity ?? granularity;

    // ── Detalle de un período de la evolución ──

    /** Abre el detalle de un período con atenciones; al cerrarlo el foco vuelve a `opener` (el gráfico o el chip). */
    const openPeriod = useCallback((point: TimelinePoint, opener: HTMLElement | null) => {
        detailOpenerRef.current = opener;
        setDetail({ point, granularity: shownGranularity });
    }, [shownGranularity]);

    const closeDetail = useCallback(() => {
        setDetail(null);
        const opener = detailOpenerRef.current;
        detailOpenerRef.current = null;
        // Cuando el panel ya se cerró; si el gráfico o el chip ya no están, no hay a dónde volver
        if (opener) window.setTimeout(() => { if (opener.isConnected) opener.focus(); }, 0);
    }, []);

    /**
     * «Ver solo este día»: el rango del período pasa a ser el de la pantalla, como un período personalizado. Si el rango
     * ya era ese no cambia nada; si no, queda el rango anterior para «Volver a …» bajo los filtros.
     */
    const viewDetailRange = useCallback((from: string, to: string, serviceId: number) => {
        const current = draftRef.current;
        const sameRange = !!current.from && !!current.to && isoDate(current.from) === from && isoDate(current.to) === to;
        if (!sameRange) {
            const preset = customPeriod ? null : activePreset(current.from, current.to, today);
            let label = preset ? intl.formatMessage({ id: PRESET_NAME_ID[preset] }) : '';
            if (!label && current.from && current.to) {
                const withYear = periodNeedsYear(current.from, current.to, today);
                label = intl.formatMessage({ id: 'PeriodFieldRange' },
                    { from: formatShortDate(current.from, withYear), to: formatShortDate(current.to, withYear) });
            }
            setRangeOrigin({ previousFrom: current.from, previousTo: current.to, custom: customPeriod, label, from, to });
            setCustomPeriod(true);
        }
        if (!sameRange || serviceId !== ALL) {
            // Con un servicio, igual que tocar su fila en la tabla de servicios: las fechas y el filtro de una sola vez
            const next = { ...current, from: parseIsoDate(from), to: parseIsoDate(to), ...(serviceId !== ALL ? { serviceId } : {}) };
            const settled = serviceId !== ALL && options ? reconcileSelection(options, next, 'serviceId', mine) : next;
            setDraftNow(settled);
            applyDraft(settled, true);
        }
        closeDetail();
    }, [customPeriod, today, intl, options, mine, setDraftNow, applyDraft, closeDetail]);

    /** «Volver a {período anterior}»: devuelve las fechas de antes de «Ver solo este día» (los demás filtros quedan como están). */
    const returnToPreviousRange = useCallback(() => {
        if (!rangeOrigin) return;
        setCustomPeriod(rangeOrigin.custom);
        const next = { ...draftRef.current, from: rangeOrigin.previousFrom, to: rangeOrigin.previousTo };
        setDraftNow(next);
        applyDraft(next, true);
        setRangeOrigin(null);
    }, [rangeOrigin, setDraftNow, applyDraft]);

    /** El enlace «Volver a …» solo vale mientras se siga viendo el rango que salió del detalle. */
    const originActive = rangeOrigin !== null && !!draft.from && !!draft.to
        && isoDate(draft.from) === rangeOrigin.from && isoDate(draft.to) === rangeOrigin.to;
    useEffect(() => {
        if (rangeOrigin && !originActive) setRangeOrigin(null);
    }, [rangeOrigin, originActive]);

    const detailFilter = useMemo(() => (applied ? toFilter(applied, mine) : null), [applied, mine]);
    /** Atenciones de un período promedio, para comparar el detalle: por día (días con atenciones) y por semana o mes del gráfico. */
    const averageAttentions = useMemo(() => {
        const current = data?.summary.current;
        const drawn = timeline.filter(point => point.attentions > 0);
        return {
            perDay: current && current.activeDays > 0 ? current.attentions / current.activeDays : 0,
            perPeriod: drawn.length > 0 ? drawn.reduce((sum, point) => sum + point.attentions, 0) / drawn.length : 0,
        };
    }, [data, timeline]);
    const serviceRows = useMemo(() => {
        if (!data) return [];
        const occupancyById = new Map(data.occupancy.map(item => [item.serviceId, item]));
        return data.services.map(service => toServiceRow(service, occupancyById.get(service.serviceId)));
    }, [data]);
    /**
     * Ranking y avisos del contexto de los filtros: las filas del backend ya vienen acotadas por tipo, especialidad y
     * código; si hay un servicio o agenda elegido, solo cuentan las suyas. La tabla, en cambio, sigue mostrando todos.
     */
    const scopedRanking = useMemo(() => {
        if (!rankingRows) return [];
        return applied ? scopeToService(rankingRows, applied.serviceId) : rankingRows;
    }, [rankingRows, applied]);
    const ranking = useMemo(() => scopedRanking.slice(0, WAIT_RANKING_ROWS), [scopedRanking]);
    /** Aviso general: parte de las atenciones esperó más de una hora (no es de un servicio, así que no tiene causa). */
    const wait60 = useMemo<Wait60Notice | null>(() => {
        const current = data?.summary.current;
        if (!current || current.attentions <= 0) return null;
        const ratio = current.waitOver60 / current.attentions;
        return hasWait60Notice(ratio) ? { ratio, count: current.waitOver60, total: current.attentions } : null;
    }, [data]);
    /**
     * Nivel de cada servicio para la tabla, el del ranking (que calcula el backend, sin el filtro de servicio). El
     * ranking solo trae los servicios con nivel distinto de «En regla» o con alguna causa: con el ranking ya cargado y
     * completo, uno con `ALERT_MIN_ATTENTIONS` atenciones o más que no tiene fila está «En regla». Mientras el ranking
     * carga o falló, o si tiene más filas de las que se pidieron, los servicios sin fila no llevan nivel.
     */
    const serviceLevels = useMemo(() => {
        const levels = new Map<number, WaitLevel>();
        if (rankingRows === null || rankingFailed || rankingLoading) return levels;
        rankingRows.forEach(row => levels.set(row.serviceId, RANKING_LEVEL[row.level]));
        if (rankingTotal <= rankingRows.length) {
            serviceRows.forEach(row => {
                if (row.attentions >= ALERT_MIN_ATTENTIONS && !levels.has(row.serviceId)) levels.set(row.serviceId, 'ok');
            });
        }
        return levels;
    }, [rankingRows, rankingFailed, rankingLoading, rankingTotal, serviceRows]);
    /** Cuántos avisos tiene el período: las filas del ranking (el total del backend, si no hay una agenda elegida) y el general. */
    const noticeCount = (applied && applied.serviceId !== ALL ? scopedRanking.length : Math.max(rankingTotal, scopedRanking.length))
        + (wait60 ? 1 : 0);
    const rankingStatus: WaitRankingStatus = rankingFailed ? 'failed' : rankingRows === null ? 'loading' : 'ready';
    /** "del 1/9 al 30/9": el período de los datos que se ven. */
    const periodText = useMemo(() => {
        if (!applied) return '';
        const date = (iso: string) => parseIsoDate(iso).toLocaleDateString(intl.locale);
        return intl.formatMessage({ id: 'ServicesStatisticsAlertsPeriod' },
            { from: date(applied.initialDate), to: date(applied.finalDate) });
    }, [applied, intl]);
    /** "Consultorios · Pediatría · del 1/9 al 30/9": el resumen de filtros que va en el PDF y el Excel. */
    const exportContext = useMemo(() => [
        ...CHIP_ORDER.map(key => filterNames[key]).filter((name): name is string => !!name), periodText,
    ].join(' · '), [filterNames, periodText]);

    /** Los datos que se ven no son los de los filtros: se están consultando o el cambio espera el debounce. */
    const busy = loading || pending;
    const showError = loadError || optionsFailed;
    const periodUnit = intl.formatMessage({ id: PERIOD_UNIT_ID[shownGranularity] });
    const timelineTitle = intl.formatMessage({ id: 'ServicesStatisticsTimelineTitle' },
        { measure: intl.formatMessage({ id: MEASURE_NAME_ID[measure] }), period: periodUnit });

    /**
     * Exportar a PDF o Excel lo que se está viendo: los filtros de los datos que se ven, el resumen de filtros y la
     * agrupación de la evolución. Mientras carga, o si el período no tiene atenciones, no hay qué exportar.
     */
    const handleExport = useCallback((format: ExportFormat) => {
        if (!applied || !data) return Promise.reject(new Error('No hay una consulta para exportar'));
        return exportServiceStatistics(toFilter(applied, mine), format, exportContext, shownGranularity);
    }, [applied, data, mine, exportContext, shownGranularity]);
    const exportDisabledReason = busy ? intl.formatMessage({ id: 'ServicesStatisticsExportLoading' })
        : !data || !applied ? intl.formatMessage({ id: 'ServicesStatisticsExportNoQuery' })
            : !hasData && !agendasOnly ? intl.formatMessage({ id: modeMessageId(mode, 'ExportNoAttentions') })
                : undefined;

    const title = intl.formatMessage({ id: modeMessageId(mode, 'Title') });

    /** La pestaña «Agendas»: va en la pantalla completa y, sin atenciones en el período, sola. */
    const occupancyPanel = data && applied ? (
        <OccupancyPanel
            agendas={agendas}
            failed={data.occupancyFailed}
            selectedId={applied.serviceId}
            onToggle={serviceId => toggleScope('serviceId', serviceId)}
            schedule={{
                status: scheduleStatus,
                data: scheduleLoaded ? scheduleResult?.data ?? null : null,
                onRetry: retrySchedule,
            }}
            mode={mode}
        />
    ) : null;

    if (accessProblem) {
        return (
            <div className="nv-scope nv-page services-statistics">
                <header className="nv-page-head">
                    <h1 className="nv-h1">{title}</h1>
                </header>
                <p className="nv-note" role="status">
                    <i className="pi pi-info-circle" aria-hidden="true" />
                    {intl.formatMessage({
                        id: accessProblem === 'professionalRequired'
                            ? 'ProfessionalPerformanceNotProfessional' : 'ServicesStatisticsAccessDenied',
                    })}
                </p>
            </div>
        );
    }

    return (
        <div className="nv-scope nv-page services-statistics">
            <NvTooltips />
            <header className="nv-page-head">
                <div className="nv-head-row">
                    <h1 className="nv-h1">{title}</h1>
                    <ExportMenu
                        onExport={handleExport}
                        disabled={exportDisabledReason !== undefined}
                        disabledReason={exportDisabledReason}
                        fallbackFileName={mine ? 'estadisticas-de-mis-agendas' : 'estadisticas-de-atencion'}
                    />
                </div>
            </header>

            <StatisticsFilters
                mode={mode}
                draft={draft}
                errors={errors}
                optionsReady={options !== null}
                templateTypeOptions={templateTypeOptions}
                specialityOptions={specialityOptions}
                professionalOptions={professionalOptions}
                serviceOptions={serviceOptions}
                visible={visible}
                period={period}
                rangeText={rangeText}
                chips={chips}
                moreOpen={moreOpen}
                moreActive={moreActive}
                onToggleMore={() => setMoreOpen(open => !open)}
                onChangeFilter={changeFilter}
                onDateChange={changeDate}
                onDatesCommit={commitDates}
                onPeriodChange={selectPeriod}
                onRemoveChip={key => applyScope({ [key]: ALL })}
                onClear={clearFilters}
            />

            {rangeOrigin && originActive && (
                <div className="ss-range-back">
                    <Button type="button" link icon="pi pi-angle-double-left"
                        label={intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailReturn' }, { period: rangeOrigin.label })}
                        onClick={returnToPreviousRange} />
                </div>
            )}

            <div className="ss-results" aria-busy={busy}>
                {busy && (
                    <div className="ss-loading" role="status">
                        <div className="nv-loading ss-loading__box">
                            <ProgressSpinner strokeWidth="4" style={{ width: 24, height: 24 }} />
                            <span>{intl.formatMessage({ id: 'ServicesStatisticsLoading' })}</span>
                        </div>
                    </div>
                )}
                {showError && !busy && (
                    <div className="nv-card ss-error" role="alert">
                        <p className="nv-note">
                            <i className="pi pi-info-circle" aria-hidden="true" />
                            {intl.formatMessage({ id: 'ServicesStatisticsQueryError' })}
                        </p>
                        <Button type="button" className="buttonMain2" outlined
                            label={intl.formatMessage({ id: 'ServicesStatisticsRetry' })} onClick={retry} />
                    </div>
                )}
                {!showError && data && applied && agendasOnly && (
                    <div className={classNames('ss-content', { 'ss-blurred': busy })}>
                        <p className="nv-note" role="status">
                            <i className="pi pi-info-circle" aria-hidden="true" />
                            {intl.formatMessage({ id: modeMessageId(mode, 'OnlyAgendas') })}
                        </p>
                        <TabView activeIndex={0}>
                            <TabPanel header={intl.formatMessage({ id: 'ServicesStatisticsTabOccupancy' })}>
                                {occupancyPanel}
                            </TabPanel>
                        </TabView>
                    </div>
                )}
                {!showError && data && applied && !hasData && !agendasOnly && (
                    <div className={classNames('ss-no-results', { 'ss-blurred': busy })}>
                        <p className="nv-muted">{intl.formatMessage({ id: modeMessageId(mode, 'Empty') })}</p>
                        {chips.length > 0 && (
                            <Button type="button" link label={intl.formatMessage({ id: 'ServicesStatisticsClearFilters' })}
                                onClick={clearFilters} />
                        )}
                    </div>
                )}
                {!showError && data && applied && hasData && (
                    <div className={classNames('ss-content', { 'ss-blurred': busy })}>
                        <div className="ss-kpis-box">
                            <StatisticsKpis summary={data.summary} agendas={agendas} occupancyFailed={data.occupancyFailed}
                                mode={mode} />
                        </div>

                        <WaitRanking
                            rows={ranking}
                            status={rankingStatus}
                            refreshing={rankingLoading && rankingRows !== null}
                            selectedServiceId={applied.serviceId}
                            noticeCount={noticeCount}
                            onViewService={serviceId => applyScope({ serviceId })}
                            onOpenAlerts={() => setAlertsOpen(true)}
                            onRetry={() => setRankingReload(key => key + 1)}
                            mode={mode}
                        />

                        {mine && (
                            <SpecialityBenchmark benchmark={data.benchmark} failed={data.benchmarkFailed}
                                current={data.summary.current} />
                        )}

                        <div>
                            <TabView activeIndex={activeTab} onTabChange={(e: TabViewTabChangeEvent) => setActiveTab(e.index)}>
                                <TabPanel header={intl.formatMessage({ id: modeMessageId(mode, 'TabServices') })}>
                                    <ServicesTable
                                        rows={serviceRows}
                                        selectedId={applied.serviceId}
                                        onToggle={serviceId => toggleScope('serviceId', serviceId)}
                                        levels={serviceLevels}
                                        mode={mode}
                                    />
                                </TabPanel>
                                <TabPanel header={intl.formatMessage({ id: 'ServicesStatisticsTabTimeline' })}>
                                    <section className="nv-section" aria-labelledby="ss-timeline-title">
                                        <div className="ss-card-head ss-card-head--row">
                                            <h2 className="nv-section-title" id="ss-timeline-title">{timelineTitle}</h2>
                                            <div className="ss-chart-controls">
                                                <MeasureSelector id="ss-timeline-measure" labelId="ServicesStatisticsMeasureBy"
                                                    value={measure} onChange={setMeasure} />
                                                <GranularitySelector id="ss-timeline-granularity" value={granularity}
                                                    onChange={value => setGranularityChoice({ range: rangeKey, value })} />
                                            </div>
                                        </div>
                                        {granularity === 'DAY' && queryDays > MAX_DAYS_BY_WEEK && (
                                            <p className="nv-help" role="status">
                                                {intl.formatMessage({ id: 'ServicesStatisticsGroupDayTooLong' })}
                                            </p>
                                        )}
                                        {timelineFailed ? (
                                            <div className="ss-empty" role="status">
                                                <p className="nv-muted">{intl.formatMessage({ id: 'ServicesStatisticsTimelineFailed' })}</p>
                                                <Button type="button" link label={intl.formatMessage({ id: 'ServicesStatisticsRetry' })}
                                                    onClick={() => setTimelineReload(key => key + 1)} />
                                            </div>
                                        ) : (
                                            <div className={classNames({ 'ss-blurred': timelineLoading })}>
                                                <TimelineEvolution points={timeline} granularity={shownGranularity} measure={measure}
                                                    ariaLabel={timelineTitle} current={data.summary.current}
                                                    onOpenPeriod={openPeriod} />
                                            </div>
                                        )}
                                    </section>
                                </TabPanel>
                                <TabPanel header={intl.formatMessage({ id: 'ServicesStatisticsTabHourly' })}>
                                    <HourlyTab hourly={data.hourly} peakHour={data.summary.peakHour}
                                        measure={measure} onMeasureChange={setMeasure} />
                                </TabPanel>
                                <TabPanel header={intl.formatMessage({ id: 'ServicesStatisticsTabOccupancy' })}>
                                    {occupancyPanel}
                                </TabPanel>
                                <TabPanel header={intl.formatMessage({ id: 'ServicesStatisticsTabPriorities' })}>
                                    <div className="nv-section">
                                        <PriorityList
                                            priorities={data.priorities}
                                            selectedId={applied.priorityId}
                                            onToggle={priorityId => toggleScope('priorityId', priorityId)}
                                        />
                                    </div>
                                </TabPanel>
                            </TabView>
                        </div>

                        <AlertsDialog
                            visible={alertsOpen}
                            onHide={() => setAlertsOpen(false)}
                            rows={scopedRanking}
                            wait60={wait60}
                            selectedServiceId={applied.serviceId}
                            onViewService={serviceId => { setAlertsOpen(false); applyScope({ serviceId }); }}
                            mode={mode}
                        />

                        {detailFilter && (
                            <PeriodDetailSidebar
                                target={detail}
                                filter={detailFilter}
                                measure={measure}
                                overall={data.summary.current}
                                services={data.services}
                                averageAttentions={averageAttentions}
                                scheduleAgendaId={scheduleAgendaId}
                                mode={mode}
                                onViewRange={viewDetailRange}
                                onHide={closeDetail}
                                onAccessProblem={setAccessProblem}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
